import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

// headers are the headers to be sent with each request
// It is important not to calculate them beforehand, because they use the
// identification token that changes over time.
export interface ApiConfig {
  baseUrl: string;
  headers: (url: string) => HttpHeaders;
}

export interface EndpointConstructor {
  getEndpoint<TResponse>(url: string): () => Observable<TResponse>;

  getEndpointWithParams<TUrlParams, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<TResponse>;

  deleteEndpoint<TResponse>(url: string): () => Observable<TResponse>;

  deleteEndpointWithParams<TUrlParams, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<TResponse>;

  postEndpoint<TData, TResponse>(
    url: string,
  ): (dto: TData) => Observable<TResponse>;

  postEndpointWithParams<TUrlParams, TData, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<TResponse>;

  putEndpoint<TData, TResponse>(
    url: string,
  ): (dto: TData) => Observable<TResponse>;

  putEndpointWithParams<TUrlParams, TData, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<TResponse>;

  getTextEndpoint(url: string): () => Observable<string>;

  getTextEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<string>;

  deleteTextEndpoint(url: string): () => Observable<string>;

  deleteTextEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<string>;

  postTextEndpoint<TData>(url: string): (dto: TData) => Observable<string>;

  postTextEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<string>;

  putTextEndpoint<TData>(url: string): (dto: TData) => Observable<string>;

  putTextEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<string>;

  getBlobEndpoint(url: string): () => Observable<Blob>;

  getBlobEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<Blob>;

  deleteBlobEndpoint(url: string): () => Observable<Blob>;

  deleteBlobEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<Blob>;

  postBlobEndpoint<TData>(url: string): (dto: TData) => Observable<Blob>;

  postBlobEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<Blob>;

  putBlobEndpoint<TData>(url: string): (dto: TData) => Observable<Blob>;

  putBlobEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<Blob>;
}

export class Api implements EndpointConstructor {
  private baseUrl: string;
  private headers: (url: string) => HttpHeaders;
  constructor(
    private httpClient: HttpClient,
    config: ApiConfig,
  ) {
    this.baseUrl = config.baseUrl;
    this.headers = config.headers;
  }

  private get<TResponse>(url: string): Observable<TResponse> {
    return this.httpClient.get<TResponse>(`${this.baseUrl}${url}`, {
      headers: this.headers(url),
    });
  }

  private delete<TResponse>(url: string): Observable<TResponse> {
    return this.httpClient.delete<TResponse>(`${this.baseUrl}${url}`, {
      headers: this.headers(url),
    });
  }

  private post<TData, TResponse>(
    url: string,
    dto: TData,
  ): Observable<TResponse> {
    return this.httpClient.post<TResponse>(`${this.baseUrl}${url}`, dto, {
      headers: this.headers(url),
    });
  }

  private put<TData, TResponse>(
    url: string,
    dto: TData,
  ): Observable<TResponse> {
    return this.httpClient.put<TResponse>(`${this.baseUrl}${url}`, dto, {
      headers: this.headers(url),
    });
  }

  private getText(url: string): Observable<string> {
    return this.httpClient.get(`${this.baseUrl}${url}`, {
      headers: this.headers(url),
      responseType: 'text',
    });
  }

  private deleteText(url: string): Observable<string> {
    return this.httpClient.delete(`${this.baseUrl}${url}`, {
      headers: this.headers(url),
      responseType: 'text',
    });
  }

  private postText<TData>(url: string, dto: TData): Observable<string> {
    return this.httpClient.post(`${this.baseUrl}${url}`, dto, {
      headers: this.headers(url),
      responseType: 'text',
    });
  }

  private putText<TData>(url: string, dto: TData): Observable<string> {
    return this.httpClient.put(`${this.baseUrl}${url}`, dto, {
      headers: this.headers(url),
      responseType: 'text',
    });
  }

  private getBlob(url: string): Observable<Blob> {
    return this.httpClient.get(`${this.baseUrl}${url}`, {
      headers: this.headers(url),
      responseType: 'blob',
    });
  }

  private deleteBlob(url: string): Observable<Blob> {
    return this.httpClient.delete(`${this.baseUrl}${url}`, {
      headers: this.headers(url),
      responseType: 'blob',
    });
  }

  private postBlob<TData>(url: string, dto: TData): Observable<Blob> {
    return this.httpClient.post(`${this.baseUrl}${url}`, dto, {
      headers: this.headers(url),
      responseType: 'blob',
    });
  }

  private putBlob<TData>(url: string, dto: TData): Observable<Blob> {
    return this.httpClient.put(`${this.baseUrl}${url}`, dto, {
      headers: this.headers(url),
      responseType: 'blob',
    });
  }

  getEndpoint<TResponse>(url: string): () => Observable<TResponse> {
    return () => this.get<TResponse>(url);
  }

  getEndpointWithParams<TUrlParams, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<TResponse> {
    return params => this.get<TResponse>(url(params));
  }

  deleteEndpoint<TResponse>(url: string): () => Observable<TResponse> {
    return () => this.delete<TResponse>(url);
  }

  deleteEndpointWithParams<TUrlParams, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<TResponse> {
    return params => this.delete<TResponse>(url(params));
  }

  postEndpoint<TData, TResponse>(
    url: string,
  ): (dto: TData) => Observable<TResponse> {
    return dto => this.post<TData, TResponse>(url, dto);
  }

  postEndpointWithParams<TUrlParams, TData, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<TResponse> {
    return (params, dto) => this.post<TData, TResponse>(url(params), dto);
  }

  putEndpoint<TData, TResponse>(
    url: string,
  ): (dto: TData) => Observable<TResponse> {
    return dto => this.put<TData, TResponse>(url, dto);
  }

  putEndpointWithParams<TUrlParams, TData, TResponse>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<TResponse> {
    return (params, dto) => this.put<TData, TResponse>(url(params), dto);
  }

  getTextEndpoint(url: string): () => Observable<string> {
    return () => this.getText(url);
  }

  getTextEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<string> {
    return params => this.getText(url(params));
  }

  deleteTextEndpoint(url: string): () => Observable<string> {
    return () => this.deleteText(url);
  }

  deleteTextEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<string> {
    return params => this.deleteText(url(params));
  }

  postTextEndpoint<TData>(url: string): (dto: TData) => Observable<string> {
    return dto => this.postText<TData>(url, dto);
  }

  postTextEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<string> {
    return (params, dto) => this.postText<TData>(url(params), dto);
  }

  putTextEndpoint<TData>(url: string): (dto: TData) => Observable<string> {
    return dto => this.putText<TData>(url, dto);
  }

  putTextEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<string> {
    return (params, dto) => this.putText<TData>(url(params), dto);
  }

  getBlobEndpoint(url: string): () => Observable<Blob> {
    return () => this.getBlob(url);
  }

  getBlobEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<Blob> {
    return params => this.getBlob(url(params));
  }

  deleteBlobEndpoint(url: string): () => Observable<Blob> {
    return () => this.deleteBlob(url);
  }

  deleteBlobEndpointWithParams<TUrlParams>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams) => Observable<Blob> {
    return params => this.deleteBlob(url(params));
  }

  postBlobEndpoint<TData>(url: string): (dto: TData) => Observable<Blob> {
    return dto => this.postBlob<TData>(url, dto);
  }

  postBlobEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<Blob> {
    return (params, dto) => this.postBlob<TData>(url(params), dto);
  }

  putBlobEndpoint<TData>(url: string): (dto: TData) => Observable<Blob> {
    return dto => this.putBlob<TData>(url, dto);
  }

  putBlobEndpointWithParams<TUrlParams, TData>(
    url: (params: TUrlParams) => string,
  ): (params: TUrlParams, dto: TData) => Observable<Blob> {
    return (params, dto) => this.putBlob<TData>(url(params), dto);
  }
}
